// components/CreateDeploymentModal.tsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DeploymentResponse } from '../types';
import DeploymentSuccess from './DeploymentSuccess';

const BACKEND_URL = "https://instant-deployerapi.pionedata.com";

interface CreateDeploymentModalProps {
  onClose: () => void;
}

const CreateDeploymentModal: React.FC<CreateDeploymentModalProps> = ({ onClose }) => {
  const { fetchDeployments } = useAuth();
  const [subdomain, setSubdomain] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const validate = (): string | null => {
    if (!subdomain) return "Please enter a subdomain";
    if (!/^[a-zA-Z0-9-]+$/.test(subdomain)) return "Subdomain can contain letters, numbers or hyphens";
    if (!file) return "Please upload an HTML file";
    if (!file.name.endsWith(".html")) return "Only .html files allowed";
    return null;
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleDeploy = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("");
    setUrl("");
    setProgress(0);

    const err = validate();
    if (err) {
      setInputError(err);
      return;
    }
    setInputError("");

    setIsLoading(true);
    setStatus("Deploying...");

    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append("subdomain", subdomain);
      if (file) fd.append("file", file);

      const resp = await fetch(`${BACKEND_URL}/deploy`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: fd
      });

      let fakeProgress = 0;
      const progInterval = setInterval(() => {
        fakeProgress += 10;
        if (fakeProgress > 90) fakeProgress = 90;
        setProgress(fakeProgress);
      }, 150);

      if (!resp.ok) {
        clearInterval(progInterval);
        const errorData = await resp.json();
        throw new Error(errorData.detail || 'Deployment failed');
      }

      const json: DeploymentResponse = await resp.json();
      clearInterval(progInterval);
      setProgress(100);

      if (json.status === "success") {
        setUrl(json.url);
        setStatus("Deployed Successfully!");
        setShowSuccess(true);
        await fetchDeployments();
      } else {
        setStatus(json.detail || "Error occurred");
      }

    } catch (e: any) {
      setStatus("Error: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAnother = () => {
    setShowSuccess(false);
    setUrl('');
    setStatus('');
    setSubdomain('');
    setFile(null);
    setProgress(0);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div style={{
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        background: 'white',
        borderRadius: '20px',
        padding: '32px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
        overflow: 'auto'
      }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ color: '#2563eb', fontSize: '28px', fontWeight: 800, margin: 0 }}>
              Create New Deployment
            </h2>
            <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0 0' }}>
              Deploy your HTML site to pionedata.com instantly
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleDeploy} style={{ display: "grid", gap: '24px' }}>

          {/* Subdomain field */}
          <div>
            <label style={{ fontWeight: 700, color: "#1f2937", marginBottom: '8px', display: "block", fontSize: '16px' }}>
              Subdomain
            </label>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.trim())}
                placeholder="mysite"
                style={{
                  flex: 1,
                  padding: "14px 16px",
                  border: inputError.includes("subdomain") ? "2px solid #f87171" : "2px solid #e5e7eb",
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: "none",
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = inputError.includes("subdomain") ? "#f87171" : "#e5e7eb"}
              />
              <span style={{ marginLeft: '12px', fontWeight: 700, color: "#6b7280", fontSize: '16px' }}>
                .pionedata.com
              </span>
            </div>
          </div>

          {/* Drag & Drop file upload */}
          <div>
            <label style={{ fontWeight: 700, color: "#1f2937", marginBottom: '8px', display: "block", fontSize: '16px' }}>
              HTML File
            </label>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              style={{
                padding: '32px',
                borderRadius: '12px',
                border: file ? "3px solid #10b981" : (dragActive ? "3px dashed #3b82f6" : "3px dashed #d1d5db"),
                background: file ? "#f0fdf4" : (dragActive ? "#eff6ff" : "#f9fafb"),
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
              onClick={() => document.getElementById('modalFileInput')?.click()}
            >
              <div style={{ fontSize: '48px', color: file ? "#10b981" : "#6b7280", marginBottom: '16px' }}>
                {file ? "✅" : "📁"}
              </div>
              <p style={{ 
                fontSize: '16px', 
                color: file ? "#065f46" : "#374151", 
                fontWeight: file ? 700 : 600, 
                marginBottom: '8px' 
              }}>
                {file ? `Selected: ${file.name}` : "Drop your HTML file here"}
              </p>
              <p style={{ fontSize: '14px', color: "#6b7280", margin: 0 }}>
                {file ? "Click to change file" : "or click to select a file"}
              </p>
              <input
                id="modalFileInput"
                type="file"
                accept=".html"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: "none" }}
              />
              {file && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'inline-block'
                }}>
                  File Ready: {Math.round(file.size / 1024)} KB
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {isLoading && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Deploying...</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#3b82f6' }}>{progress}%</span>
              </div>
              <div style={{ height: '8px', background: "#e5e7eb", borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  height: "100%",
                  width: progress + "%",
                  background: "linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)",
                  borderRadius: '4px',
                  transition: "width 0.3s ease"
                }}></div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              onClick={onClose}
              style={{
                padding: "12px 20px",
                background: "#f3f4f6",
                color: "#374151",
                border: "none",
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: "pointer"
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isLoading}
              style={{
                padding: "12px 24px",
                background: isLoading ? "#93c5fd" : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                color: "white",
                border: "none",
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: isLoading ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
              }}
            >
              {isLoading ? "Deploying..." : "Deploy Site"}
            </button>
          </div>
        </form>

        {/* Status messages */}
        {inputError && (
          <div style={{ 
            marginTop: '16px', 
            background: "#fef2f2", 
            border: "1px solid #fecaca",
            padding: '12px', 
            borderRadius: '8px', 
            color: "#dc2626", 
            fontWeight: 600, 
            fontSize: '14px'
          }}>
            {inputError}
          </div>
        )}

        {status && !url && (
          <div style={{ 
            marginTop: '16px', 
            background: status.startsWith("Error") ? "#fef2f2" : "#f0fdf4", 
            border: status.startsWith("Error") ? "1px solid #fecaca" : "1px solid #bbf7d0",
            padding: '12px', 
            borderRadius: '8px', 
            color: status.startsWith("Error") ? "#dc2626" : "#16a34a", 
            fontWeight: 600,
            fontSize: '14px'
          }}>
            {status}
          </div>
        )}

        {url && showSuccess && (
          <DeploymentSuccess 
            url={url} 
            subdomain={subdomain}
            onClose={onClose}
            onCreateAnother={handleCreateAnother}
          />
        )}

        {url && !showSuccess && (
          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", 
            border: "2px solid #10b981", 
            borderRadius: '12px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎉</div>
              <strong style={{ color: "#065f46", fontSize: '18px', display: 'block', marginBottom: '12px' }}>
                Your site is live!
              </strong>
              <a 
                href={url} 
                target="_blank" 
                rel="noreferrer" 
                style={{ 
                  color: "#3b82f6", 
                  fontWeight: 700,
                  fontSize: '16px',
                  textDecoration: 'none',
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Visit {url} →
              </a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CreateDeploymentModal;